import { useState, useMemo } from 'react';
import { Button, Card, CardContent } from './core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEvaluationUser } from '../graphql/hooks/useUserDashboard';
import { useCloseMonthHours, useUsersEvaluationAdmin } from '../graphql/hooks/useAdminDashboard';
import { CloseMonthConfirmModal } from './CloseMonthConfirmModal';
import { useActiveUsers } from '../graphql/hooks/useUserDashboard';
import { useAuth } from '../graphql/hooks/useAuth';
import { SearchableSelect } from './core/SearchableSelect';

type ReportsViewNewProps = {
  selectedUserId: string | null;
  setSelectedUserId: React.Dispatch<React.SetStateAction<string | null>>;
};

export function ReportsViewNew({
  selectedUserId,
  setSelectedUserId,
}: ReportsViewNewProps) {
  const { userId, userType } = useAuth();
  const isAdmin = userType === 'admin' || userType === 'Superadmin';
  const [activeTab, setActiveTab] = useState<'uebersicht' | 'stunden'>(
    'uebersicht',
  );

  const getCurrentMonthString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthString());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const formatMonthForState = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [year, month] = selectedMonth.split('-').map(Number);

  const effectiveUserId = isAdmin ? selectedUserId ?? userId : userId;

  const { data: evaluationUser } = useEvaluationUser(
    { month, year },
  );

  const { data: evaluationAdmin } = useUsersEvaluationAdmin(
    {
      userId: effectiveUserId,
      month,
      year,
    },
  );

  const evaluation = isAdmin
    ? evaluationAdmin?.getEvaluation
    : evaluationUser;

  const { data: closeMonthHours } = useCloseMonthHours(
    isAdmin
      ? { userId: effectiveUserId, month, year }
      : { month, year }
  );

  const { data: activeUsers } = useActiveUsers();
  const userOptions = useMemo(
    () => (activeUsers ?? []).map((user) => ({ value: user.id, label: user.full_name })),
    [activeUsers]
  );
  const selectedUser = activeUsers?.find((u) => u.id === selectedUserId);


  const monthNames = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ];

  const formatMonthLabel = (value: string) => {
    const [year, month] = value.split('-');
    return `${monthNames[Number(month) - 1]} ${year}`;
  };

const monthDiff = 
  (evaluation?.hour_overview?.ist_sum_inkl_zuschlag ?? 0) -
  (evaluation?.hour_overview?.month_working_hours ?? 0) ; //-
  //(evaluation?.time_credit_overview?.fixed_time ?? 0);

  const renderMonthPicker = () => {
    const months = [
      'Jan',
      'Feb',
      'Mär',
      'Apr',
      'Mai',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Okt',
      'Nov',
      'Dez',
    ];

    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() =>
              setCurrentMonth(new Date(currentMonth.getFullYear() - 1, 0, 1))
            }
          >
            <ChevronLeft className="size-5 text-[#6b6b6b]" />
          </button>

          <div className="text-sm font-medium">
            {currentMonth.getFullYear()}
          </div>

          <button
            onClick={() =>
              setCurrentMonth(new Date(currentMonth.getFullYear() + 1, 0, 1))
            }
          >
            <ChevronRight className="size-5 text-[#6b6b6b]" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {months.map((month, index) => {
            const isSelected =
              selectedMonth ===
              formatMonthForState(
                new Date(currentMonth.getFullYear(), index, 1),
              );

            return (
              <button
                key={month}
                onClick={() => {
                  const newDate = new Date(
                    currentMonth.getFullYear(),
                    index,
                    1,
                  );

                  setSelectedMonth(formatMonthForState(newDate));
                  setCurrentMonth(newDate);
                  setShowMonthPicker(false);
                }}
                className={`h-10 rounded-md text-sm transition-colors
                  ${
                    isSelected
                      ? 'bg-[#0084c7] text-white'
                      : 'hover:bg-[#f5f5f5]'
                  }
                `}
              >
                {month}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="bg-[#f5f5f5] min-h-screen">
      <div className="bg-white border-b border-[#e9e9e9]">
        <div className="px-4 md:px-8 py-2 md:py-0 space-y-3 md:space-y-0">

          <div className="flex md:hidden overflow-x-auto gap-6 border-b border-[#e9e9e9] scrollbar-hide">
            <button
              onClick={() => setActiveTab('uebersicht')}
              className={`py-3 text-[15px] font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'uebersicht'
                  ? 'border-[#0084c7] text-[#0084c7]'
                  : 'border-transparent text-[#6b6b6b]'
              }`}
            >
              Übersicht
            </button>

            <button
              onClick={() => setActiveTab('stunden')}
              className={`py-3 text-[15px] font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'stunden'
                  ? 'border-[#0084c7] text-[#0084c7]'
                  : 'border-transparent text-[#6b6b6b]'
              }`}
            >
              Stunden
            </button>
          </div>

          <div className="flex flex-col md:hidden gap-3">

            {isAdmin && (
              <SearchableSelect
                value={selectedUserId ?? undefined}
                onChange={(id) => id && setSelectedUserId(id)}
                options={userOptions}
                placeholder="Mitarbeiter auswählen"
                searchPlaceholder="Mitarbeiter suchen..."
                noResultsText="Keine Mitarbeiter gefunden"
                className="w-full text-[14px]"
              />
            )}

            <div className="relative">
              <button
                onClick={() => setShowMonthPicker((prev) => !prev)}
                className="w-full px-4 py-2 border border-[#e9e9e9] rounded-lg text-[14px] text-[#1c1c1c] bg-white hover:border-[#0084c7]"
              >
                {formatMonthLabel(selectedMonth)}
              </button>

              {showMonthPicker && (
                <div className="absolute box-border left-0 mt-2 w-full bg-white border border-[#e9e9e9] rounded-[12px] shadow-lg p-4 z-30">
                  {renderMonthPicker()}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between">

            <div className="flex items-center gap-6">
              <div className="flex gap-5">
                <button
                  onClick={() => setActiveTab('uebersicht')}
                  className={`py-4 px-2 text-[15px] font-medium border-b-2 transition-colors ${
                    activeTab === 'uebersicht'
                      ? 'border-[#0084c7] text-[#0084c7]'
                      : 'border-transparent text-[#6b6b6b]'
                  }`}
                >
                  Übersicht
                </button>

                <button
                  onClick={() => setActiveTab('stunden')}
                  className={`py-4 px-2 text-[15px] font-medium border-b-2 transition-colors ${
                    activeTab === 'stunden'
                      ? 'border-[#0084c7] text-[#0084c7]'
                      : 'border-transparent text-[#6b6b6b]'
                  }`}
                >
                  Stunden
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4">

              {isAdmin && (
                <SearchableSelect
                  value={selectedUserId ?? undefined}
                  onChange={(id) => id && setSelectedUserId(id)}
                  options={userOptions}
                  placeholder="Mitarbeiter auswählen"
                  searchPlaceholder="Mitarbeiter suchen..."
                  noResultsText="Keine Mitarbeiter gefunden"
                  className="text-[14px]"
                />
              )}

              <div className="relative">
                <button
                  onClick={() => setShowMonthPicker((prev) => !prev)}
                  className="px-4 py-2 border border-[#e9e9e9] rounded-lg text-[14px] text-[#1c1c1c] bg-white hover:border-[#0084c7]"
                >
                  {formatMonthLabel(selectedMonth)}
                </button>

                {showMonthPicker && (
                  <div className="absolute right-0 mt-2 w-[260px] bg-white border border-[#e9e9e9] rounded-[12px] shadow-lg p-4 z-30">
                    {renderMonthPicker()}
                  </div>
                )}
              </div>

            </div>
          </div>

        </div>
      </div>

        <div className="p-4 md:p-8">
        {activeTab === 'stunden' && (
          <div className="max-w-[1400px] mx-auto">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className='hidden md:table-row-group'>
                      <tr className="border-b border-[#e9e9e9]">
                        <th className="text-left py-4 px-6 text-[#6b6b6b] text-[13px] font-medium uppercase tracking-wide">
                          Datum
                        </th>
                        <th className="text-left py-4 px-6 text-[#6b6b6b] text-[13px] font-medium uppercase tracking-wide">
                          Gesamt Stunden
                        </th>
                        <th className="text-left py-4 px-6 text-[#6b6b6b] text-[13px] font-medium uppercase tracking-wide">
                          Notdienst
                        </th>
                      </tr>
                    </thead>
                    <tbody className="hidden md:table-row-group">
                      {closeMonthHours?.map((entry, index) => (
                        <tr
                          key={entry.date}
                          className={`border-b border-[#e9e9e9] hover:bg-[#f5f5f5] transition-colors ${
                            index % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'
                          }`}
                        >
                          <td className="py-4 px-6 text-[14px] text-[#1c1c1c]">
                            {entry.date}
                          </td>

                          <td className="py-4 px-6 text-[14px] text-[#1c1c1c]">
                            {entry.total_hours} Std
                          </td>

                          <td className="py-4 px-6 text-[14px] text-[#1c1c1c]">
                            {entry.emergency ? 'Ja' : 'Nein'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                    <div className="md:hidden divide-y divide-[#e9e9e9]">
                      {closeMonthHours?.map((entry) => (
                        <div
                          key={entry.date}
                          className="p-4 space-y-3 bg-white"
                        >
                          <div className='flex flex-col gap-1'>
                            <p className="text-[12px] text-[#6b6b6b]">Datum</p>
                            <p className="text-[14px] font-medium text-[#1c1c1c]">
                              {entry.date}
                            </p>
                          </div>

                          <div className="flex justify-between">
                            <div className='flex flex-col gap-1'>
                              <p className="text-[12px] text-[#6b6b6b]">
                                Gesamt Stunden
                              </p>
                              <p className="text-[14px] text-[#1c1c1c]">
                                {entry.total_hours} Std
                              </p>
                            </div>

                            <div className="text-right flex flex-col">
                              <p className="text-[12px] text-[#6b6b6b]">
                                Notdienst
                              </p>

                              <span
                                className={` text-center px-2 py-1 rounded-full text-[12px] font-medium
                                  ${
                                    entry.emergency
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                              >
                                {entry.emergency ? 'Ja' : 'Nein'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'uebersicht' && (
          <div className="max-w-[1400px] mx-auto space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardContent>
                  <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-6 pb-3 border-b border-[#e9e9e9]">
                    Urlaubskonto
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Urlaub aktueller Monat
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.holiday_overview.holiday_used_month ?? 0}{' '}
                        Tage
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Jahresurlaub
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.holiday_overview.holiday_total ?? 0} Tage
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Resturlaub
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.holiday_overview.holiday_open ?? 0} Tage |{' '}
                        {evaluation?.holiday_overview.holiday_pending ?? 0} Tage
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-6 pb-3 border-b border-[#e9e9e9]">
                    Zeitkonto
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Zeitguthaben:
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.time_credit_overview.time_credit ?? 0} Std
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Überstunden abgefeiert:
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.time_credit_overview.overtime_hours ?? 0}{' '}
                        Std
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Nach abschluss ausbezahlt:
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.time_credit_overview.overtime_paid ?? 0}{' '}
                        Std
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Überstundenabzug:
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.time_credit_overview?.overtime_deduction ?? 0} Std
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Fester Abzug:
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.time_credit_overview?.fixed_time ?? 0} Std
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Zeitguthaben nach Abzug:
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.time_credit_overview?.remaining_time_credit ?? 0} Std
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-6 pb-3 border-b border-[#e9e9e9]">
                    Wochenendarbeiten
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Notdienst
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {
                          evaluation?.weekend_work_overview
                            .weekend_emergency_service
                        }{' '}
                        Tage
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Baustelle
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {' '}
                        {(evaluation?.weekend_work_overview
                          ?.weekend_time_trackings ?? 0) -
                          (evaluation?.weekend_work_overview
                            ?.weekend_emergency_service ?? 0)}{' '}
                        Tage
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">Gesamt</span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {
                          evaluation?.weekend_work_overview
                            .weekend_time_trackings
                        }{' '}
                        Tage
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardContent>
                  <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-6 pb-3 border-b border-[#e9e9e9]">
                    Gesonderte Auszahlungen
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Nachtarbeit
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.separate_payment_overview?.night_hours ??
                          0}{' '}
                        Std
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Notdienst
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.separate_payment_overview
                          ?.emergency_service_payment ?? 0}{' '}
                        Euro
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">Spesen</span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.separate_payment_overview
                          ?.expenses_payment ?? 0}{' '}
                        Euro
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-6 pb-3 border-b border-[#e9e9e9]">
                    Stundenkonto
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Ist Stunden
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {(
                          (evaluation?.hour_overview?.regular_hours ?? 0) +
                          (evaluation?.hour_overview?.overtime_hours ?? 0) +
                          (evaluation?.hour_overview?.sunday_hours ?? 0) +
                          (evaluation?.hour_overview?.night_hours ?? 0)
                        )}{' '}
                        Std
                      </span>
                    </div>
                    <div className="pl-4">
                      <div className="text-[#6b6b6b] text-[13px]">
                        {`100% ${evaluation?.hour_overview.regular_hours ?? 0} Std | 125% ${evaluation?.hour_overview.overtime_hours ?? 0} Std | nacht: ${evaluation?.hour_overview.night_hours ?? 0} Std | sonntag: ${evaluation?.hour_overview.sunday_hours ?? 0} Std`}
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Ist std inkl. Zuschlag
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">{evaluation?.hour_overview?.ist_sum_inkl_zuschlag ?? 0} Std</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Soll Stunden
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.hour_overview.month_working_hours ?? 0} Std
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Arbeitstage/Monat
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.hour_overview.month_working_days ?? 0} Tage
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Geleistete Tage/Monat
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.hour_overview.tracked_working_days ?? 0}{' '}
                        Tage
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Krankentage
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.hour_overview.sickdays ?? 0} Tage
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6b6b6b] text-[14px]">
                        Wochenende
                      </span>
                      <span className="text-[#1c1c1c] text-[16px] font-medium">
                        {evaluation?.hour_overview.weekend_work_days ?? 0} Tage
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            {!evaluation?.month_closed_user && (
              <Button
                variant="primary"
                onClick={() => {
                  setConfirmOpen(true);
                }}
                className="flex items-center gap-2"
              >
                Monat Abschließen
              </Button>
            )}
            {confirmOpen && (
              <CloseMonthConfirmModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                monthLabel={formatMonthLabel(selectedMonth)}
                hoursDiff={monthDiff}
                month={month}
                year={year}
                user_id={effectiveUserId}
              />
            )}
          </div>
        )}
      </div>
        <div className="text-center mb-4">
          <p className="text-[#9ca3af] text-[12px]">
            © 2026 ENT-TRO GmbH by{' '}
            <a
              href="https://curvedesign.de"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-inherit"
            >
              CurveDesign GmbH
            </a>
          </p>
        </div>
    </div>
  );
}
